import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { enqueueSnackbar } from '~/utils/notifications';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import RenameDatasetDialog from '~/components/Dialogs/RenameDatasetDialog';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { ImportResult } from '~/domain/services/dicomProcessor';
import { reformatBlockedReason } from '~/domain/services/imageService';
import type { ReformatTarget } from '~/domain/services/imageService';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import ReformatDialog from '../ReformatDialog';
import { REFORMAT_BLOCKED_MESSAGE_IDS } from './reformatBlockedMessages';
import {
    reformatTargetFor,
    useReformatBlockedReason,
} from './useReformatBlockedReason';

type Role = 'primary' | 'secondary' | null;

interface ImageDeleteTarget {
    image: AvailableImage;
    affectedSets: StructureSet[];
}

const ImageCard = React.memo(function ImageCard({
    image,
    role,
    isSelected,
    onSelect,
    onReformat,
    onRename,
    onDelete,
    onDownload,
}: {
    image: AvailableImage;
    role: Role;
    isSelected?: boolean;
    onSelect?: () => void;
    onReformat?: (img: AvailableImage) => void;
    onRename?: (img: AvailableImage) => void;
    onDelete?: (img: AvailableImage) => void;
    onDownload?: (img: AvailableImage) => void;
}) {
    const intl = useIntl();
    const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

    const isReformat = !!image.needsReformat;
    const showReformat = isSelected && onReformat;

    // Mirror of the domain predicate: the control names why it is refused
    // rather than letting the reformat start and fail on the first read.
    // `reformatBlockedReason` covers the progressively loaded image too, so
    // an image whose slices are still arriving is refused here and in the
    // dialog by the same test.
    const blockedReason = useReformatBlockedReason(image);
    const reformatTooltip = blockedReason
        ? t(REFORMAT_BLOCKED_MESSAGE_IDS[blockedReason])
        : t('reformatTooltip');
    // Exporting a partly arrived image would write a truncated series to
    // disk under the source's identity. `Image3D.getSlicesAsDicomBlobs`
    // throws on it; the disabled control carries the reason instead.
    const imageGate = useImageCompletenessGate(image.imageObject);

    const handleReformatClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onReformat?.(image);
        },
        [onReformat, image]
    );

    return (
        <Box
            onClick={onSelect}
            sx={{
                position: 'relative',
                cursor: onSelect ? 'pointer' : 'default',
                bgcolor: isSelected
                    ? 'rgba(144, 202, 249, 0.08)'
                    : 'transparent',
                '&:hover': onSelect
                    ? {
                          bgcolor: isSelected
                              ? 'rgba(144, 202, 249, 0.12)'
                              : 'rgba(144, 202, 249, 0.06)',
                      }
                    : undefined,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'background-color 0.15s ease-in-out',
                    zIndex: 1,
                },
                transition: 'all 0.15s ease-in-out',
            }}
        >
            <AvailableImageItem
                image={image}
                isSecondary={role === 'secondary'}
                isSelected={false}
                onReformat={onReformat ? () => onReformat(image) : undefined}
                onDownload={
                    onDownload && !imageGate.isBlocked
                        ? () => onDownload(image)
                        : undefined
                }
                onRename={onRename ? () => onRename(image) : undefined}
                onDelete={onDelete ? () => onDelete(image) : undefined}
            />

            {showReformat && (
                <Box
                    display="flex"
                    gap={0.5}
                    px={2}
                    pb={0.75}
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {isReformat && (
                        <Tooltip title={reformatTooltip} placement="right">
                            {/* A disabled MUI Button emits no pointer events,
                                so the tooltip that says WHY needs a wrapper
                                that still does. */}
                            <span>
                                <Button
                                    data-testid="reformat-image-button"
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    onClick={handleReformatClick}
                                    disabled={blockedReason !== null}
                                    sx={{ fontSize: '0.65rem' }}
                                >
                                    <FormattedMessage id="reformatImage" />
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {!isReformat && image.imageObject && (
                        <Tooltip title={reformatTooltip} placement="right">
                            <span>
                                <Button
                                    data-testid="reformat-loaded-image-button"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleReformatClick}
                                    disabled={blockedReason !== null}
                                    sx={{ fontSize: '0.65rem' }}
                                >
                                    <FormattedMessage id="reformatLoadedImage" />
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default function ImageList() {
    const intl = useIntl();
    const session = useSessionOrNull();
    const { availableImages, primaryImage, handleChangePrimaryImage } =
        usePrimaryImage(true);
    const { selectedSecondaryImage } = useSecondaryImage();
    const { initializeEnvironment } = useViewportEnvironment();
    const { deleteImage, deleteRawSeries, getAffectedStructureSets } =
        useDeleteDataset();

    const [reformatTarget, setReformatTarget] = useState<ReformatTarget | null>(
        null
    );
    const [deleteTarget, setDeleteTarget] = useState<ImageDeleteTarget | null>(
        null
    );
    const [renameTarget, setRenameTarget] = useState<Image3D | null>(null);
    // The series description is patient information: a rename open when it
    // is hidden closes without saving. The rows withhold the action itself.
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    useEffect(() => {
        if (!showSensitiveInfo) {
            setRenameTarget(null);
        }
    }, [showSensitiveInfo]);

    const getRole = useCallback(
        (image: AvailableImage): Role => {
            if (image.seriesInstanceUID === primaryImage?.seriesUid) {
                return 'primary';
            }
            if (
                image.imageObject?.dicomUid === selectedSecondaryImage?.dicomUid
            ) {
                return 'secondary';
            }
            return null;
        },
        [primaryImage?.seriesUid, selectedSecondaryImage?.dicomUid]
    );

    const handleImageClick = useCallback(
        (image: AvailableImage) => {
            handleChangePrimaryImage(image);
        },
        [handleChangePrimaryImage]
    );

    const handleOpenDelete = useCallback(
        (image: AvailableImage) => {
            if (!image.imageObject && !image.rawSeries) {
                return;
            }
            const affectedSets = image.imageObject
                ? getAffectedStructureSets(image.imageObject)
                : [];
            setDeleteTarget({ image, affectedSets });
        },
        [getAffectedStructureSets]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!deleteTarget) {
            return;
        }
        const { image } = deleteTarget;
        if (image.imageObject) {
            deleteImage(image.imageObject);
        } else if (image.rawSeries) {
            deleteRawSeries(image.rawSeries);
        }
        setDeleteTarget(null);
    }, [deleteTarget, deleteImage, deleteRawSeries]);

    const handleOpenRename = useCallback((image: AvailableImage) => {
        if (image.imageObject) {
            setRenameTarget(image.imageObject);
        }
    }, []);

    const handleConfirmRename = useCallback(
        (label: string) => {
            if (renameTarget && session) {
                session.imageService.rename({ id: renameTarget.id }, label);
            }
            setRenameTarget(null);
        },
        [renameTarget, session]
    );

    const handleOpenReformat = useCallback(
        (image: AvailableImage) => {
            const target = reformatTargetFor(image);
            if (!target) {
                return;
            }
            // The disabled button is not the only way in: a card that needs
            // reformatting opens it on plain click too
            // (AvailableImageItem's handleClick), which no disabled state
            // covers. Refuse here as well, and say why — that route shows
            // no tooltip.
            const blocked = reformatBlockedReason(target);
            if (blocked !== null) {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: REFORMAT_BLOCKED_MESSAGE_IDS[blocked],
                    }),
                    { variant: 'warning' }
                );
                return;
            }
            setReformatTarget(target);
        },
        [intl]
    );

    const handleCloseReformat = useCallback(() => {
        setReformatTarget(null);
    }, []);

    const handleReformatComplete = useCallback(
        (importResult: ImportResult) => {
            initializeEnvironment(importResult);
            setReformatTarget(null);
        },
        [initializeEnvironment]
    );

    const deleteDialogMessage = useMemo(() => {
        if (!deleteTarget) {
            return '';
        }
        const base = intl.formatMessage({ id: 'deleteImageConfirm' });
        if (deleteTarget.affectedSets.length === 0) {
            return base;
        }
        const names = deleteTarget.affectedSets
            .map((ss) => ss.dicomStructureSet.structureSetLabel)
            .join(', ');
        return `${base} ${intl.formatMessage(
            { id: 'deleteImageStructureSetWarning' },
            { structureSets: names }
        )}`;
    }, [deleteTarget, intl]);

    if (availableImages.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={2} py={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="images" />
                    </Typography>
                </Box>
                <Box>
                    {availableImages.map((img, index) => {
                        const role = getRole(img);
                        const isPrimary = role === 'primary';
                        return (
                            <React.Fragment key={img.seriesInstanceUID}>
                                {index > 0 && <Divider />}
                                <ImageCard
                                    image={img}
                                    role={role}
                                    isSelected={isPrimary}
                                    onSelect={
                                        !img.needsReformat
                                            ? () => handleImageClick(img)
                                            : undefined
                                    }
                                    onReformat={handleOpenReformat}
                                    onRename={
                                        img.imageObject && session
                                            ? handleOpenRename
                                            : undefined
                                    }
                                    onDelete={
                                        img.imageObject || img.rawSeries
                                            ? handleOpenDelete
                                            : undefined
                                    }
                                    onDownload={
                                        !img.imageObject && !img.rawSeries
                                            ? handleImageClick
                                            : undefined
                                    }
                                />
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteImage' })}
                message={deleteDialogMessage}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />

            {renameTarget && showSensitiveInfo && (
                <RenameDatasetDialog
                    open
                    title={intl.formatMessage({ id: 'renameImage' })}
                    initialValue={renameTarget.seriesMetadata.seriesDescription}
                    onCancel={() => setRenameTarget(null)}
                    onConfirm={handleConfirmRename}
                />
            )}

            {reformatTarget && (
                <ReformatDialog
                    open
                    onClose={handleCloseReformat}
                    target={reformatTarget}
                    onReformatComplete={handleReformatComplete}
                />
            )}
        </>
    );
}
